import type { OrderDeliveryInput } from '@matix/types';
import type { DeliveryDraft } from '../../store/cart';
import { moneyPrecision } from '../../store/currency';
import { roundMoney } from '@matix/utils';

/**
 * Turn the cart's delivery draft into the `delivery` sub-object the server
 * requires on every `order_type === 2` request.
 *
 * ONE builder, because two screens book a delivery now: the
 * DeliveryConfirmModal's "create unpaid" button and — since the cashier can
 * also collect the money up front — the ordinary PaymentModal, reached from
 * that modal's "Take payment". A second hand-rolled copy of this shape is how
 * the two would quietly disagree about, say, whether an absent driver is
 * omitted or sent as null.
 *
 * Null when the draft has no customer: the server refuses a delivery without
 * one (DELIVERY_PAYLOAD_REQUIRED), so the caller must keep the cashier on the
 * delivery form rather than post something it will reject.
 */
export function buildDeliveryPayload(draft: DeliveryDraft): OrderDeliveryInput | null {
  if (draft.customerId == null) return null;
  return {
    customer_id: draft.customerId,
    // Both ids are omitted rather than sent as null when unset — the server
    // treats absent and null identically, and an absent key keeps the wire
    // payload readable in the request logs. The address is optional (a
    // customer with none on file still books); the driver is commonly
    // assigned by the dispatcher after the order is taken.
    ...(draft.addressId != null ? { address_id: draft.addressId } : {}),
    ...(draft.driverId != null ? { driver_id: draft.driverId } : {}),
    delivery_fees: deliveryFeeOf(draft),
    delivery_date: draft.deliveryDate,
    ...(draft.referenceNo.trim() ? { reference_no: draft.referenceNo.trim() } : {}),
  };
}

/**
 * The fee, rounded the way every other money figure in the till is.
 *
 * It is ALSO what `buildCreateOrderRequest` must be given as `deliveryFee`:
 * the server prices a delivery order from `delivery.delivery_fees`, but the
 * client's `expected_subtotal / expected_tax / expected_total` are computed
 * from the builder's own argument. Passing 0 there left `expected_total` short
 * by exactly the fee and the server refused the order with
 * "Order total mismatch".
 */
export function deliveryFeeOf(draft: DeliveryDraft): number {
  return roundMoney(draft.deliveryFees || 0, moneyPrecision());
}

/**
 * The fee a delivery order starts with once an address is picked — the legacy
 * till's `ApplyDeliveryFees` (order_functions_definitions.js) plus the invoice
 * source override it applies at payment:
 *
 *   1. the active invoice source's own `DeliveryFees`, when it has one;
 *   2. else the address's zone charge (`GL_Zones.Delivery_charges` via
 *      `GL_Area.ZoneId`, or the district's own fee — resolved server-side as
 *      `CustomerAddress.delivery_fee`);
 *   3. else 0, as the legacy till does for a district with no zone.
 *
 * The cashier can still overwrite it in the field; this only decides the
 * starting value. Never applied to an order reopened for editing — that keeps
 * the fee it was sold with (the legacy function returns early there too).
 */
export function autoDeliveryFee(
  address: { delivery_fee?: number | null } | null | undefined,
  sourceFee: number | null,
): number {
  const fee = sourceFee ?? address?.delivery_fee ?? 0;
  return roundMoney(Number(fee) || 0, moneyPrecision());
}
